import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  projectsApi,
  Project,
  ProjectGroupNode,
  GroupedProjectsParams,
} from '../../../api/projectsApi';
import { clientsApi } from '../../../api/clientsApi';
import { usersApi } from '../../../api/usersApi';
import { downloadBlob } from '../../../utils/helpers';
import {
  Search,
  Download,
  FileSpreadsheet,
  Refresh,
  ChevronRight,
  ChevronDown,
  X,
  Filter,
  StarFilled,
  Columns,
} from '../Icons';
import { Button } from '../Button';
import { SortIcon } from '../SortIcon';
import { useSorting, applySorting } from '../../../hooks/useSorting';
import { FavoritesDropdown } from '../FavoritesDropdown';
import { FavoriteConfig } from '../../../api/favoritesApi';
import { ColumnSearchBar, SearchChip } from '../ColumnSearchBar';
import './GroupedProjects.css';

/* ── Column Definitions ── */
interface ColumnConfig {
  key: string;
  label: string;
  defaultVisible: boolean;
}

const PROJECT_COLUMNS: ColumnConfig[] = [
  { key: 'name', label: 'Project', defaultVisible: true },
  { key: 'code', label: 'Code', defaultVisible: true },
  { key: 'client', label: 'Client', defaultVisible: true },
  { key: 'manager', label: 'Manager', defaultVisible: true },
  { key: 'type', label: 'Type', defaultVisible: true },
  { key: 'billable', label: 'Billable', defaultVisible: true },
  { key: 'status', label: 'Status', defaultVisible: true },
  { key: 'duration', label: 'Duration', defaultVisible: true },
  { key: 'description', label: 'Description', defaultVisible: false },
  { key: 'project_category', label: 'Project Category', defaultVisible: false },
  { key: 'allocated_hours', label: 'Allocated Hours', defaultVisible: false },
  { key: 'hours_spent', label: 'Hours Spent', defaultVisible: false },
  { key: 'sales_person', label: 'Sales Person', defaultVisible: false },
  { key: 'stage', label: 'Stage', defaultVisible: false },
  { key: 'tags', label: 'Tags', defaultVisible: false },
];

const DEFAULT_VISIBLE_COLUMNS = PROJECT_COLUMNS.filter(c => c.defaultVisible).map(c => c.key);
const STORAGE_KEY_PROJECT_COLUMNS = 'gp_visible_columns';

const PROJECT_SEARCH_FIELDS = [
  { key: 'project', label: 'Project' },
  { key: 'code', label: 'Code' },
  { key: 'client', label: 'Client' },
  { key: 'manager', label: 'Manager' },
];

/* ── Group By options ── */
type GroupByField = 'client' | 'manager' | 'type' | 'billable' | 'status';

interface GroupByOption {
  key: GroupByField;
  label: string;
}

const GROUP_BY_OPTIONS: GroupByOption[] = [
  { key: 'client', label: 'Client' },
  { key: 'manager', label: 'Manager' },
  { key: 'type', label: 'Project Type' },
  { key: 'billable', label: 'Billable' },
  { key: 'status', label: 'Status' },
];

function getGroupByLabel(key: GroupByField): string {
  return GROUP_BY_OPTIONS.find((o) => o.key === key)?.label ?? key;
}

const PROJECT_TYPE_LABELS: Record<string, string> = {
  adm: 'ADM',
  tnm: 'T&M',
  staff_aug: 'Staff Aug',
  inhouse: 'In-house',
  digital_marketing: 'Digital Marketing',
  upwork: 'Upwork',
  outsource: 'Outsource',
};

/* ── Props ── */
interface GroupedProjectsProps {
  pageTitle: string;
}

/* ── Component ── */
export function GroupedProjects({ pageTitle }: GroupedProjectsProps) {
  // Group-by state
  const [activeGroupBy, setActiveGroupBy] = useState<GroupByField[]>([]);
  const [groups, setGroups] = useState<ProjectGroupNode[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Expanded groups
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Detail rows (loaded on expand for leaf groups)
  const [detailRows, setDetailRows] = useState<Record<string, { projects: Project[]; total: number }>>({});
  const [detailLoading, setDetailLoading] = useState<Set<string>>(new Set());

  // Filters
  const [searchChips, setSearchChips] = useState<SearchChip[]>([]);
  const [statusFilter, setStatusFilter] = useState('active');
  const [clientFilter, setClientFilter] = useState('');
  const [managerFilter, setManagerFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [billableFilter, setBillableFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Active favorite
  const [activeFavoriteName, setActiveFavoriteName] = useState<string | null>(null);

  // Filter data
  const [filterClients, setFilterClients] = useState<{ id: number; name: string }[]>([]);
  const [filterManagers, setFilterManagers] = useState<{ id: number; name: string }[]>([]);

  // Export
  const [exportLoading, setExportLoading] = useState(false);

  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_PROJECT_COLUMNS);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return DEFAULT_VISIBLE_COLUMNS;
      }
    }
    return DEFAULT_VISIBLE_COLUMNS;
  });
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const columnMenuRef = useRef<HTMLDivElement>(null);

  // Dropdowns
  const [showGroupByMenu, setShowGroupByMenu] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const groupByMenuRef = useRef<HTMLDivElement>(null);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  // Flat view with pagination
  const [flatProjects, setFlatProjects] = useState<Project[]>([]);
  const [flatTotal, setFlatTotal] = useState(0);
  const [flatPage, setFlatPage] = useState(1);
  const [flatPages, setFlatPages] = useState(1);
  const PER_PAGE = 50;

  // Sorting state for flat view
  type FlatProjectSortKey = 'name' | 'code' | 'client' | 'manager' | 'type' | 'billable' | 'status';
  const { sortConfig: flatSortConfig, handleSort: handleFlatSort, getSortDirection, isColumnSorted } = useSorting<FlatProjectSortKey>();

  // Apply sorting to flat projects
  const sortedFlatProjects = useMemo(() => {
    return applySorting(flatProjects, flatSortConfig, (project, key) => {
      switch (key) {
        case 'name': return project.name || '';
        case 'code': return project.code || '';
        case 'client': return project.client_name || '';
        case 'manager': return project.manager_name || '';
        case 'type': return project.project_type || '';
        case 'billable': return project.is_billable ? 'Yes' : 'No';
        case 'status': return project.status || '';
        default: return '';
      }
    });
  }, [flatProjects, flatSortConfig]);

  // Close menus on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (groupByMenuRef.current && !groupByMenuRef.current.contains(e.target as Node)) {
        setShowGroupByMenu(false);
      }
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setShowExportMenu(false);
      }
      if (columnMenuRef.current && !columnMenuRef.current.contains(e.target as Node)) {
        setShowColumnMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Save visible columns to localStorage when changed
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_PROJECT_COLUMNS, JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  // Column toggle handlers
  const handleColumnToggle = (columnKey: string) => {
    setVisibleColumns(prev => {
      if (prev.includes(columnKey)) {
        if (prev.length === 1) return prev;
        return prev.filter(c => c !== columnKey);
      }
      return [...prev, columnKey];
    });
  };

  const handleSelectAllColumns = () => {
    setVisibleColumns(PROJECT_COLUMNS.map(c => c.key));
  };

  const handleResetColumns = () => {
    setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
  };

  // Available options
  const availableOptions = useMemo(
    () => GROUP_BY_OPTIONS.filter((o) => !activeGroupBy.includes(o.key)),
    [activeGroupBy],
  );

  /* ── Load filter data ── */
  useEffect(() => {
    const loadFilterData = async () => {
      try {
        const [clientsRes, managersRes] = await Promise.all([
          clientsApi.getClients(),
          usersApi.getManagers(),
        ]);
        setFilterClients((clientsRes as any).clients?.map((c: any) => ({ id: c.id, name: c.name })) ?? []);
        setFilterManagers(
          Array.isArray(managersRes)
            ? (managersRes as any).map((m: any) => ({ id: m.id, name: m.name }))
            : (managersRes as any).managers?.map((m: any) => ({ id: m.id, name: m.name })) ?? [],
        );
      } catch {
        // silently fail
      }
    };
    loadFilterData();
  }, []);

  /* ── Build filter params ── */
  const buildFilterParams = useCallback(() => {
    const params: any = {};
    const colSearches: Record<string, string[]> = {};
    searchChips.forEach(chip => {
      if (!colSearches[chip.field]) colSearches[chip.field] = [];
      colSearches[chip.field].push(chip.value);
    });
    Object.entries(colSearches).forEach(([field, values]) => {
      params[`search_${field}`] = values.join('||');
    });
    if (statusFilter) params.status = statusFilter;
    if (clientFilter) params.client_id = Number(clientFilter);
    if (managerFilter) params.manager_id = Number(managerFilter);
    if (typeFilter) params.project_type = typeFilter;
    if (billableFilter) params.is_billable = billableFilter;
    return params;
  }, [searchChips, statusFilter, clientFilter, managerFilter, typeFilter, billableFilter]);

  /* ── Fetch grouped data ── */
  const fetchGroupedData = useCallback(async () => {
    if (activeGroupBy.length === 0) return;
    try {
      setLoading(true);
      setError(null);
      const params: GroupedProjectsParams = {
        group_by: activeGroupBy.join(','),
        ...buildFilterParams(),
      };
      const res = await projectsApi.getProjectsGrouped(params);
      setGroups(res.groups);
      setTotalCount(res.total);
      setExpandedGroups(new Set());
      setDetailRows({});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [activeGroupBy, buildFilterParams]);

  /* ── Fetch flat data ── */
  const fetchFlatData = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      const res = await projectsApi.getProjects({
        ...buildFilterParams(),
        page,
        per_page: PER_PAGE,
      });
      setFlatProjects(res.projects);
      setFlatTotal(res.total || 0);
      setFlatPage(res.page ?? 1);
      setFlatPages(res.pages ?? 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [buildFilterParams]);

  /* ── Trigger fetch on filter/groupBy change ── */
  useEffect(() => {
    if (activeGroupBy.length > 0) {
      fetchGroupedData();
    } else {
      setFlatPage(1); // Reset to page 1 when filters change
      fetchFlatData(1);
    }
    // eslint-disable-next-line
  }, [activeGroupBy, searchChips, statusFilter, clientFilter, managerFilter, typeFilter, billableFilter]);

  /* ── Add/remove group by ── */
  const addGroupByField = (field: GroupByField) => {
    setActiveGroupBy((prev) => [...prev, field]);
    setShowGroupByMenu(false);
  };

  const removeGroupByField = (field: GroupByField) => {
    setActiveGroupBy((prev) => {
      const idx = prev.indexOf(field);
      if (idx === -1) return prev;
      return prev.slice(0, idx);
    });
  };

  /* ── Toggle expand ── */
  const toggleExpand = (key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  /* ── Load detail rows for leaf ── */
  const loadDetailRows = useCallback(
    async (groupPath: string, filterOverrides: Record<string, any>) => {
      setDetailLoading((prev) => new Set(prev).add(groupPath));
      try {
        const res = await projectsApi.getProjects({
          ...buildFilterParams(),
          ...filterOverrides,
          per_page: 500,
        });
        setDetailRows((prev) => ({
          ...prev,
          [groupPath]: { projects: res.projects, total: res.projects.length },
        }));
      } catch {
        // silently fail
      } finally {
        setDetailLoading((prev) => {
          const next = new Set(prev);
          next.delete(groupPath);
          return next;
        });
      }
    },
    [buildFilterParams],
  );

  /* ── Build filter overrides from group node ── */
  const buildFilterOverrides = (node: ProjectGroupNode, parentOverrides: Record<string, any> = {}): Record<string, any> => {
    const overrides = { ...parentOverrides };
    if (node.field === 'client') overrides.client_id = node.field_value !== 'none' ? Number(node.field_value) : undefined;
    else if (node.field === 'manager') overrides.manager_id = node.field_value !== 'none' ? Number(node.field_value) : undefined;
    else if (node.field === 'type') overrides.project_type = node.field_value !== 'none' ? node.field_value : undefined;
    else if (node.field === 'billable') overrides.is_billable = node.field_value;
    else if (node.field === 'status') overrides.status = node.field_value;
    return overrides;
  };

  /* ── Search ── */


  /* ── Export ── */
  const handleExport = async () => {
    try {
      setExportLoading(true);
      const blob = await projectsApi.exportProjects({
        format: 'xlsx',
        ...buildFilterParams(),
      });
      downloadBlob(blob, `projects_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch {
      // ignore
    } finally {
      setExportLoading(false);
      setShowExportMenu(false);
    }
  };

  /* ── Format date ── */
  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr + 'T00:00:00');
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  /* ── Render project row ── */
  const renderProjectRow = (project: Project) => (
    <tr key={project.id} className="gp-detail-row">
      {visibleColumns.includes('name') && <td>{project.name}</td>}
      {visibleColumns.includes('code') && <td>{project.code || '-'}</td>}
      {visibleColumns.includes('client') && <td>{project.client_name || '-'}</td>}
      {visibleColumns.includes('manager') && <td>{project.manager_name || '-'}</td>}
      {visibleColumns.includes('type') && <td>{PROJECT_TYPE_LABELS[project.project_type || ''] || project.project_type || '-'}</td>}
      {visibleColumns.includes('billable') && (
        <td>
          <span className={`gp-billable-badge ${project.is_billable ? 'gp-billable-yes' : 'gp-billable-no'}`}>
            {project.is_billable ? 'Yes' : 'No'}
          </span>
        </td>
      )}
      {visibleColumns.includes('status') && (
        <td>
          <span className={`gp-status-badge gp-status-${project.status}`}>
            {project.status}
          </span>
        </td>
      )}
      {visibleColumns.includes('duration') && <td>{formatDate(project.start_date)} – {formatDate(project.end_date)}</td>}
      {visibleColumns.includes('description') && <td className="gp-description-cell">{project.description || '-'}</td>}
      {visibleColumns.includes('project_category') && <td>-</td>}
      {visibleColumns.includes('allocated_hours') && <td>{(project as any).monthly_hours || '-'}</td>}
      {visibleColumns.includes('hours_spent') && <td>-</td>}
      {visibleColumns.includes('sales_person') && <td>{(project as any).sales_person_name || '-'}</td>}
      {visibleColumns.includes('stage') && <td>-</td>}
      {visibleColumns.includes('tags') && <td>-</td>}
    </tr>
  );

  /* ── Render group node recursively ── */
  const renderGroupNode = (
    node: ProjectGroupNode,
    depth: number,
    parentOverrides: Record<string, any> = {},
  ): React.ReactNode => {
    const isExpanded = expandedGroups.has(node.key);
    const hasSubgroups = node.sub_groups && node.sub_groups.length > 0;
    const isLeaf = !hasSubgroups;
    const overrides = buildFilterOverrides(node, parentOverrides);
    const groupPath = node.key;

    const handleToggle = () => {
      toggleExpand(node.key);
      if (isLeaf && !isExpanded && !detailRows[groupPath]) {
        loadDetailRows(groupPath, overrides);
      }
    };

    const detailData = detailRows[groupPath];
    const isDetailLoading = detailLoading.has(groupPath);

    return (
      <React.Fragment key={node.key}>
        <tr
          className={`gp-group-header-row depth-${Math.min(depth, 3)}`}
          onClick={handleToggle}
          style={{ cursor: 'pointer' }}
        >
          <td colSpan={Math.max(visibleColumns.length - 1, 1)} style={{ paddingLeft: `${depth * 24 + 12}px` }}>
            <span className="gp-group-expand-icon">
              {isExpanded ? <ChevronDown /> : <ChevronRight />}
            </span>
            <span className="gp-group-label">{node.label}</span>
            <span className="gp-group-count">({node.count})</span>
          </td>
          <td></td>
        </tr>

        {isExpanded && hasSubgroups && node.sub_groups!.map((sub) => renderGroupNode(sub, depth + 1, overrides))}

        {isExpanded && isLeaf && (
          <>
            {isDetailLoading && (
              <tr className="gp-detail-loading-row">
                <td colSpan={visibleColumns.length}>Loading...</td>
              </tr>
            )}
            {detailData && detailData.projects.map((p) => renderProjectRow(p))}
            {detailData && detailData.projects.length === 0 && (
              <tr>
                <td colSpan={visibleColumns.length} className="gp-empty-cell">No projects in this group</td>
              </tr>
            )}
          </>
        )}
      </React.Fragment>
    );
  };

  /* ── RENDER ── */
  return (
    <div className="grouped-projects-page">
      <div className="gp-page-header">
        <h1>{pageTitle}</h1>
      </div>

      {/* Toolbar */}
      <div className="gp-toolbar">
        <div className="gp-toolbar-left">
          {/* Active favorite chip */}
          {activeFavoriteName && (
            <span className="gt-fav-chip">
              <StarFilled className="gt-fav-chip-star" />
              {activeFavoriteName}
              <button
                className="gt-fav-chip-remove"
                onClick={() => {
                  setActiveFavoriteName(null);
                  setSearchChips([]);
                  setActiveGroupBy([]);
                  setStatusFilter('active');
                  setClientFilter('');
                  setManagerFilter('');
                  setTypeFilter('');
                  setBillableFilter('');
                }}
                title="Remove favorite"
              >
                <X />
              </button>
            </span>
          )}

          {activeGroupBy.length > 0 && (
            <div className="gp-groupby-chips">
              <span className="gp-chip-icon">⊞</span>
              {activeGroupBy.map((field, idx) => (
                <React.Fragment key={field}>
                  {idx > 0 && <span className="gp-chip-separator">&gt;</span>}
                  <span className="gp-chip">
                    {getGroupByLabel(field)}
                    <button className="gp-chip-remove" onClick={() => removeGroupByField(field)} title="Remove">
                      <X />
                    </button>
                  </span>
                </React.Fragment>
              ))}
            </div>
          )}

          <ColumnSearchBar
            fields={PROJECT_SEARCH_FIELDS}
            chips={searchChips}
            onChipsChange={setSearchChips}
            placeholder="Search projects..."
          />
        </div>

        <div className="gp-toolbar-right">
          <Button
            variant={showFilters ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter />
            Filters
          </Button>

          <div className="gp-dropdown-container" ref={groupByMenuRef}>
            <Button variant="ghost" size="sm" onClick={() => setShowGroupByMenu(!showGroupByMenu)}>
              ⊞ Group By
            </Button>
            {showGroupByMenu && (
              <div className="gp-dropdown-menu gp-groupby-menu">
                {availableOptions.length === 0 ? (
                  <div className="gp-dropdown-empty">All fields selected</div>
                ) : (
                  availableOptions.map((opt) => (
                    <div key={opt.key} className="gp-dropdown-item-wrapper">
                      <button className="gp-dropdown-item" onClick={() => addGroupByField(opt.key)}>
                        {opt.label}
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          <div className="gp-dropdown-container" ref={exportMenuRef}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowExportMenu(!showExportMenu)}
              disabled={exportLoading}
            >
              <Download />
              Export
            </Button>
            {showExportMenu && (
              <div className="gp-dropdown-menu gp-export-menu">
                <button className="gp-dropdown-item" onClick={handleExport}>
                  <FileSpreadsheet />
                  Export as Excel
                </button>
              </div>
            )}
          </div>

          {/* Column Selector */}
          <div className="gp-dropdown-container" ref={columnMenuRef}>
            <Button variant="ghost" size="sm" onClick={() => setShowColumnMenu(!showColumnMenu)}>
              <Columns />
              Columns
            </Button>
            {showColumnMenu && (
              <div className="gp-dropdown-menu gp-column-menu">
                <div className="gp-column-header">
                  <span>Toggle Columns</span>
                  <div className="gp-column-header-actions">
                    <button onClick={handleSelectAllColumns}>All</button>
                    <button onClick={handleResetColumns}>Reset</button>
                  </div>
                </div>
                <div className="gp-column-body">
                  {PROJECT_COLUMNS.map(col => (
                    <label key={col.key} className="gp-column-checkbox">
                      <input
                        type="checkbox"
                        checked={visibleColumns.includes(col.key)}
                        onChange={() => handleColumnToggle(col.key)}
                      />
                      <span>{col.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          <FavoritesDropdown
            pageKey="all_projects"
            getCurrentConfig={() => ({
              column_searches: searchChips.map(c => ({ field: c.field, label: c.label, value: c.value })),
              group_by: activeGroupBy as string[],
              visible_columns: visibleColumns,
              filters: {
                status: statusFilter,
                client_id: clientFilter,
                manager_id: managerFilter,
                type: typeFilter,
                billable: billableFilter,
              },
            })}
            applyConfig={(cfg: FavoriteConfig) => {
              if (cfg.column_searches) {
                setSearchChips(cfg.column_searches.map(c => ({ id: Math.random().toString(36).slice(2, 11), ...c })));
              } else {
                setSearchChips([]);
              }
              setActiveGroupBy((cfg.group_by || []) as GroupByField[]);
              if (cfg.visible_columns && cfg.visible_columns.length > 0) {
                setVisibleColumns(cfg.visible_columns);
              }
              const f = cfg.filters || {};
              setStatusFilter(String(f.status ?? 'active'));
              setClientFilter(String(f.client_id ?? ''));
              setManagerFilter(String(f.manager_id ?? ''));
              setTypeFilter(String(f.type ?? ''));
              setBillableFilter(String(f.billable ?? ''));
            }}
            onActiveFavoriteChange={(name) => setActiveFavoriteName(name)}
          />

          <Button
            variant="ghost"
            size="sm"
            onClick={() => (activeGroupBy.length > 0 ? fetchGroupedData() : fetchFlatData(flatPage))}
            disabled={loading}
          >
            <Refresh className={loading ? 'spin' : ''} />
            Refresh
          </Button>

          <div className="gp-total-display">
            {activeGroupBy.length > 0 ? (
              <span><strong>{totalCount}</strong> projects</span>
            ) : (
              <span>
                {flatTotal > 0 && (
                  <>
                    <strong>{((flatPage - 1) * PER_PAGE) + 1}-{Math.min(flatPage * PER_PAGE, flatTotal)}</strong> of <strong>{flatTotal}</strong>
                  </>
                )}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="gp-filters-panel">
          <div className="gp-filters-row">
            <div className="gp-filter-group">
              <label>Status</label>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="on_hold">On Hold</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="gp-filter-group">
              <label>Client</label>
              <select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}>
                <option value="">All Clients</option>
                {filterClients.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="gp-filter-group">
              <label>Manager</label>
              <select value={managerFilter} onChange={(e) => setManagerFilter(e.target.value)}>
                <option value="">All Managers</option>
                {filterManagers.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
            <div className="gp-filter-group">
              <label>Project Type</label>
              <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <option value="">All Types</option>
                <option value="adm">ADM</option>
                <option value="tnm">T&M</option>
                <option value="staff_aug">Staff Aug</option>
                <option value="inhouse">In-house</option>
              </select>
            </div>
            <div className="gp-filter-group">
              <label>Billable</label>
              <select value={billableFilter} onChange={(e) => setBillableFilter(e.target.value)}>
                <option value="">All</option>
                <option value="true">Billable</option>
                <option value="false">Non-Billable</option>
              </select>
            </div>
          </div>
          <div className="gp-filters-actions">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setStatusFilter('');
                setClientFilter('');
                setManagerFilter('');
                setTypeFilter('');
                setBillableFilter('');
              }}
            >
              Clear All
            </Button>
          </div>
        </div>
      )}

      {error && <div className="gp-error">{error}</div>}

      {/* Scrollable Table Container */}
      <div className="gp-table-scroll-container">
        <div className="gp-table-wrapper">
        <table className="gp-table">
          <thead>
            <tr>
              {visibleColumns.includes('name') && (
                <th onClick={() => handleFlatSort('name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  Project
                  <SortIcon direction={getSortDirection('name')} isActive={isColumnSorted('name')} />
                </th>
              )}
              {visibleColumns.includes('code') && (
                <th onClick={() => handleFlatSort('code')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  Code
                  <SortIcon direction={getSortDirection('code')} isActive={isColumnSorted('code')} />
                </th>
              )}
              {visibleColumns.includes('client') && (
                <th onClick={() => handleFlatSort('client')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  Client
                  <SortIcon direction={getSortDirection('client')} isActive={isColumnSorted('client')} />
                </th>
              )}
              {visibleColumns.includes('manager') && (
                <th onClick={() => handleFlatSort('manager')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  Manager
                  <SortIcon direction={getSortDirection('manager')} isActive={isColumnSorted('manager')} />
                </th>
              )}
              {visibleColumns.includes('type') && (
                <th onClick={() => handleFlatSort('type')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  Type
                  <SortIcon direction={getSortDirection('type')} isActive={isColumnSorted('type')} />
                </th>
              )}
              {visibleColumns.includes('billable') && (
                <th onClick={() => handleFlatSort('billable')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  Billable
                  <SortIcon direction={getSortDirection('billable')} isActive={isColumnSorted('billable')} />
                </th>
              )}
              {visibleColumns.includes('status') && (
                <th onClick={() => handleFlatSort('status')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  Status
                  <SortIcon direction={getSortDirection('status')} isActive={isColumnSorted('status')} />
                </th>
              )}
              {visibleColumns.includes('duration') && <th>Duration</th>}
              {visibleColumns.includes('description') && <th>Description</th>}
              {visibleColumns.includes('project_category') && <th>Project Category</th>}
              {visibleColumns.includes('allocated_hours') && <th>Allocated Hours</th>}
              {visibleColumns.includes('hours_spent') && <th>Hours Spent</th>}
              {visibleColumns.includes('sales_person') && <th>Sales Person</th>}
              {visibleColumns.includes('stage') && <th>Stage</th>}
              {visibleColumns.includes('tags') && <th>Tags</th>}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={visibleColumns.length} className="gp-loading-cell">Loading...</td>
              </tr>
            )}

            {!loading && activeGroupBy.length > 0 && groups.length === 0 && (
              <tr>
                <td colSpan={visibleColumns.length} className="gp-empty-cell">No projects found</td>
              </tr>
            )}

            {!loading && activeGroupBy.length > 0 && groups.map((g) => renderGroupNode(g, 0))}

            {!loading && activeGroupBy.length === 0 && flatProjects.length === 0 && (
              <tr>
                <td colSpan={visibleColumns.length} className="gp-empty-cell">No projects found</td>
              </tr>
            )}

            {!loading && activeGroupBy.length === 0 && sortedFlatProjects.map((p) => renderProjectRow(p))}
          </tbody>
        </table>
      </div>

      {/* Flat pagination */}
      {activeGroupBy.length === 0 && flatPages > 1 && (
        <div className="gp-pagination">
          <Button variant="ghost" size="sm" disabled={flatPage <= 1} onClick={() => fetchFlatData(flatPage - 1)}>
            Previous
          </Button>
          <span>Page {flatPage} of {flatPages}</span>
          <Button variant="ghost" size="sm" disabled={flatPage >= flatPages} onClick={() => fetchFlatData(flatPage + 1)}>
            Next
          </Button>
        </div>
      )}
      </div>
    </div>
  );
}

export default GroupedProjects;
